import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BellRing, AlertCircle, Radio } from "lucide-react";
import { Button } from "./ui/Button";

interface AnnouncementNotificationDialogProps {
  id: string;
  notificationId: string;
  title: string;
  message: string;
  priority?: {
    id: number;
    name: string;
  };
  type?: {
    id: number;
    name: string;
  };
  onClose: (id: string) => void;
}

const AnnouncementNotificationDialog: React.FC<AnnouncementNotificationDialogProps> = ({
  id,
  notificationId,
  title,
  message,
  priority,
  type,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Parse announcement data from message
  let announcementTitle = "";
  let announcementContent = "";
  
  try {
    const parsed = JSON.parse(message || "{}");
    announcementTitle = parsed.announcementTitle || "";
    announcementContent = parsed.announcementContent || "";
  } catch (e) {
    // If not JSON, use message as content
    announcementContent = message || "No content available";
  }
  
  // Use parsed title if available, otherwise fallback to notification title
  const displayTitle = announcementTitle || title || "New Announcement";
  const displayContent = announcementContent || "No content available";

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 200);
  };

  useEffect(() => {
    // Add event listener to close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const getPriorityColor = () => {
    const priorityId = priority?.id;
    switch (priorityId) {
      case 3: // High
        return "text-red-600 bg-red-50";
      case 1: // Low
        return "text-blue-600 bg-blue-50";
      default: // Medium or Normal
        return "text-yellow-600 bg-yellow-50";
    }
  };

  const getPriorityIcon = () => {
    const priorityId = priority?.id;
    switch (priorityId) {
      case 3: // High
        return <AlertCircle className="h-6 w-6" />;
      case 1: // Low
        return <Radio className="h-6 w-6" />;
      default:
        return <BellRing className="h-6 w-6" />;
    }
  };

  const getPriorityLabel = () => {
    const priorityId = priority?.id;
    switch (priorityId) {
      case 1:
        return "Low";
      case 2:
        return "Medium";
      case 3:
        return "High";
      default:
        return "Normal";
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Dialog */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg lg:max-w-2xl overflow-hidden mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with priority indicator */}
            <div className={`p-3 sm:p-4 md:p-5 ${getPriorityColor()} border-b`}>
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                  <div className="scale-90 sm:scale-100">
                    {getPriorityIcon()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold break-words">{title}</h3>
                  {priority && (
                    <p className="text-xs mt-1 opacity-80 capitalize">
                      {getPriorityLabel()} Priority
                    </p>
                  )}
                </div>
                <button
                  onClick={handleClose}
                  className="flex-shrink-0 p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4 max-h-[60vh] sm:max-h-[70vh] overflow-y-auto" style={{ textAlign: "left" }}>
              {/* Title (only show if we have announcementTitle from parsed content) */}
              {announcementTitle && (
                <div>
                  <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 break-words">
                    {displayTitle}
                  </h4>
                </div>
              )}
              
              {/* Content - Auto-detect and render HTML or plain text */}
              <div>
                {(() => {
                  // Check if content starts with HTML tags (auto-detect HTML)
                  const startsWithHtmlTag = /^\s*<[^>]+>/.test(displayContent);
                  
                  if (startsWithHtmlTag) {
                    return (
                      <>
                        <style dangerouslySetInnerHTML={{
                          __html: `
                            .announcement-notification-html h1 { font-weight: 700; font-size: clamp(1.3em, 4vw, 1.75em); color: #111827; margin: 0.75em 0 0.5em 0; }
                            .announcement-notification-html h2 { font-weight: 700; font-size: clamp(1.2em, 3.5vw, 1.5em); color: #111827; margin: 0.75em 0 0.5em 0; }
                            .announcement-notification-html h3 { font-weight: 700; font-size: clamp(1.1em, 3vw, 1.25em); color: #111827; margin: 0.75em 0 0.5em 0; }
                            .announcement-notification-html h4 { font-weight: 700; font-size: clamp(1em, 2.5vw, 1.1em); color: #111827; margin: 0.75em 0 0.5em 0; }
                            .announcement-notification-html p { margin: 0.5em 0; line-height: 1.6; font-size: clamp(0.875rem, 2vw, 1rem); }
                            .announcement-notification-html ul, .announcement-notification-html ol { margin: 0.75em 0; padding-left: 1.5em; font-size: clamp(0.875rem, 2vw, 1rem); }
                            .announcement-notification-html li { margin: 0.375em 0; line-height: 1.6; }
                            .announcement-notification-html strong { font-weight: 600; color: #374151; }
                            .announcement-notification-html em { font-style: italic; }
                            .announcement-notification-html a { color: #3b82f6; text-decoration: underline; word-break: break-word; }
                            .announcement-notification-html a:hover { color: #2563eb; }
                            .announcement-notification-html code { background: #f3f4f6; padding: 0.2em 0.4em; border-radius: 3px; font-family: monospace; font-size: 0.85em; }
                          `
                        }} />
                        <div 
                          dangerouslySetInnerHTML={{ __html: displayContent }}
                          className="text-gray-700 leading-relaxed announcement-notification-html text-sm sm:text-base"
                        />
                      </>
                    );
                  }
                  
                  return (
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm sm:text-base break-words">
                      {displayContent}
                    </p>
                  );
                })()}
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 sm:p-4 md:p-5 bg-gray-50 border-t flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <Button
                onClick={handleClose}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                Dismiss
              </Button>
              <Button
                onClick={handleClose}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
              >
                Got it
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementNotificationDialog;

